<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-row>
        <v-col cols="12" class="text-left">
          <v-toolbar-title>
            {{ $t('Справочник Размер предоплаты, %') }}
          </v-toolbar-title>
        </v-col>
      </v-row>
    </v-toolbar>
    <general-error v-if="!!generalError && !isDialogOpened" />
    <v-card-text>
      <v-row>
        <v-col>
          <v-text-field
              v-model="params.organizationName"
              v-test="{ input: 'organizationName' }"
              clearable
              :label="$t('Наименование организации')"
              hide-details
              @keydown.native.enter="focusOnBtn('searchBtn')" />
        </v-col>
        <v-col>
          <v-text-field
              v-model="params.ogrn"
              v-test="{ input: 'ogrn' }"
              clearable
              :label="$t('ОГРН/КИО')"
              hide-details
              @keydown.native.enter="focusOnBtn('searchBtn')" />
        </v-col>
        <v-col>
          <validatable-checkbox v-model="params.withHistory" :label="$t('Отобразить историю изменений')" />
        </v-col>
        <v-col
            class="text-left pr-2"
            align-self="center">
          <btn-icon-tip
              id="search"
              ref="searchBtn"
              icon="search"
              icon-color="primary"
              :tip="$t('Поиск')"
              @click="search" />
        </v-col>
      </v-row>
      <paginated-data-table
          v-if="getPrepaymentSettings"
          ref="paginatedTable"
          :headers="headers"
          :items="items"
          :loading="isLoading"
          item-key="prepaymentSettingRevisionId"
          :items-per-page="itemsPerPage"
          :total-count="totalCount"
          :locale="systemLocale"
          :request-method="getPrepaymentSettings">
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.organizationName="{ item }">
          <route-link-with-tip
              :text="item.organizationName"
              :tip="$t('Просмотр организации')"
              :to="{ path: `${urlRootPath}/organization/${item.organizationId}` }" />
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.ogrn="{ item }">
          {{ item.ogrn }}
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.prepaymentPercentage="{ item }">
          {{ item.prepaymentPercentage }}
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.creatorUserName="{ item }">
          {{ item.creatorUserName }}
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.createdDate="{ item }">
          {{ item.createdDate
            ? $moment(item.createdDate).format('DD.MM.YYYY HH:mm')
            : null }}
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.prepaymentSettingRevisionId="{ item }">
          <btn-icon-tip
              v-if="canEdite"
              icon="edit"
              :tip="$t('Редактировать')"
              @click="openEditDialog(item)" />
        </template>
      </paginated-data-table>
    </v-card-text>
    <prepayment-setting-dialog
        v-if="prepaymentSettingDialog.show"
        :dialog-prop="prepaymentSettingDialog"
        @close="closeDialog('prepaymentSettingDialog')" />
  </v-card>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import PrepaymentSettingDialog from './PrepaymentSettingDialog.vue'
  import ValidatableCheckbox from '@/components/reused/ui/ValidatableCheckbox.vue'
  import PaginatedDataTable from '@/components/reused/ui/PaginatedDataTable.vue'
  import RouteLinkWithTip from '@/components/reused/ui/RouteLinkWithTip.vue'
  import { mapActions, mapState } from 'vuex'
  import { PREPAYMENT_SETTINGS_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      GeneralError,
      PrepaymentSettingDialog,
      ValidatableCheckbox,
      PaginatedDataTable,
      RouteLinkWithTip
    },
    data () {
      return {
        isLoading: false,
        itemsPerPage: 10,
        prepaymentSettingDialog: {
          show: false,
          item: null,
          callback: null
        },
        params: {
          organizationName: null,
          ogrn: null,
          withHistory: false
        }
      }
    },
    computed: {
      ...mapState(PREPAYMENT_SETTINGS_MANAGEMENT.NAMESPACE, {
        prepaymentSettingsInfo: state => (state[PREPAYMENT_SETTINGS_MANAGEMENT.STATE.PREPAYMENT_SETTINGS] || {})
      }),
      items () {
        return this.prepaymentSettingsInfo.items || []
      },
      totalCount () {
        return this.prepaymentSettingsInfo.totalCount || 0
      },
      headers () {
        let headers = [
          { text: this.$t('Наименование организации'), align: 'start', groupable: false, sortable: true, value: 'organizationName' },
          { text: this.$t('ОГРН/КИО'), align: 'start', groupable: false, sortable: true, value: 'ogrn' },
          { text: this.$t('Размер предоплаты, %'), align: 'start', groupable: false, sortable: true, value: 'prepaymentPercentage' },
          { text: this.$t('Изменения внесены'), align: 'start', groupable: false, sortable: false, value: 'creatorUserName' },
          { text: this.$t('Дата изменений'), align: 'start', groupable: false, sortable: true, value: 'createdDate' },
          { text: this.$t('Действия'), align: 'center', groupable: false, sortable: false, value: 'prepaymentSettingRevisionId' }
        ]
        if (!this.params.withHistory) {
          headers = headers.filter(h => h.value !== 'creatorUserName' && h.value !== 'createdDate' && h.value !== 'prepaymentSettingId')
        } else {
          headers = headers.filter(h => h.value !== 'prepaymentSettingRevisionId')
        }
        return headers
      },
      isDialogOpened () {
        return this.prepaymentSettingDialog.show
      },
      canEdite () {
        return (this.isAdmin || this.isOrganizer) &&
          !this.params.withHistory
      }
    },
    watch: {
      'params.withHistory': {
        immediate: true,
        handler (newVal, oldVal) {
          if (newVal !== oldVal) {
            this.search()
          }
        }
      }
    },
    created () {
      const vm = this
      vm.clearStore().then()
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(PREPAYMENT_SETTINGS_MANAGEMENT.NAMESPACE, {
        getPrepaymentSettingsAction: PREPAYMENT_SETTINGS_MANAGEMENT.ACTIONS.GET_PREPAYMENT_SETTINGS,
        editPrepaymentSettingAction: PREPAYMENT_SETTINGS_MANAGEMENT.ACTIONS.EDIT_PREPAYMENT_SETTING,
        clearPrepaymentSettingsAction: PREPAYMENT_SETTINGS_MANAGEMENT.ACTIONS.CLEAR_PREPAYMENT_SETTINGS
      }),
      clearStore: async function () {
        return Promise.all(
          [
            this.clearPrepaymentSettingsAction()
          ])
      },
      openEditDialog: function (item) {
        this.prepaymentSettingDialog.item = item
        this.prepaymentSettingDialog.callback = this.editPrepaymentSetting
        this.prepaymentSettingDialog.show = true
      },
      search: async function () {
        if (this.$refs.paginatedTable) {
          await this.$refs.paginatedTable.resetPaginationAndGetDataFromServer()
        }
      },
      editPrepaymentSetting: async function (prepaymentSetting) {
        try {
          this.isLoading = true
          await this.editPrepaymentSettingAction(prepaymentSetting)
          this.isLoading = false
          this.$refs.paginatedTable?.getDataFromServer()
        } finally {
          this.isLoading = false
        }
      },
      getPrepaymentSettings: async function (params) {
        try {
          this.isLoading = true
          await this.getPrepaymentSettingsAction({
            ...params,
            ...this.params
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
